import { useTranslations } from "@src/packages/i18n";
import type { PlanAction, PlanStep } from "../plan.types";
import { PreCycleHome, type PreCycleHomeSection } from "./PreCycleHome";
import type { PreCyclePrepStep } from "./PreCycleHeroCard";
import type { HeroCycleEnding } from "./hero/Hero";
import { EggFreezingClinicTimelineStep } from "./EggFreezingPrepTimelineRemainingSteps";
import { SpermAnalysisSkeletonCard } from "./SpermAnalysisSkeletonCard";
import { UnderstandSpermFreezingStep } from "./SpermFreezingUnderstandStep";
import { KnowBaselineStep } from "./SpermFreezingKnowBaselineStep";
import { SpermResetProgramStep } from "./SpermFreezingResetStep";
import { SpermFreezingStartStep } from "./SpermFreezingStartStep";

type Props = {
  activeStep: PlanStep | null;
  steps: PreCyclePrepStep[];
  completedSteps: number;
  totalSteps: number;
  userAge?: number;
  /** A partner reading the patient's plan: partner wording, no patient-only steps. */
  viewerIsSupporting?: boolean;
  onAction: (action: PlanAction) => void;
  onOpenPlan: () => void;
  onEditPlan: () => void;
  /** A cycle that has just ended, announced by the hero rather than above it. */
  cycleEnding?: HeroCycleEnding | null;
};

export function PreCycleSpermFreezingHome({
  activeStep,
  steps,
  completedSteps,
  totalSteps,
  viewerIsSupporting = false,
  onAction,
  onOpenPlan,
  onEditPlan,
  cycleEnding,
}: Props) {
  const t = useTranslations();
  const heroContent = {
    eyebrow: t("homepage.pre_cycle_sperm_freezing.eyebrow"),
    // A partner reads the same plan, but the samples and the banking are the
    // patient's, so the hero speaks about the plan rather than to its owner.
    title: viewerIsSupporting
      ? t(
          "homepage.pre_cycle_sperm_freezing.supporting.understand_sperm_freezing_alongside_your_partner",
        )
      : t("homepage.pre_cycle_sperm_freezing.title"),
    cycleStartLabel: t("homepage.pre_cycle_sperm_freezing.cycle_start_label"),
    phaseDescription: viewerIsSupporting
      ? t(
          "homepage.pre_cycle_sperm_freezing.supporting.this_home_explains_the_banking_your_partner_is_planning",
        )
      : t("homepage.pre_cycle_sperm_freezing.phase_description"),
    intro: t("homepage.pre_cycle_sperm_freezing.intro"),
    openPlanAccessibilityLabel: t(
      "homepage.pre_cycle_sperm_freezing.open_plan_a11y",
    ),
    planAccessibilityLabel: t(
      "homepage.pre_cycle_sperm_freezing.toggle_plan_a11y",
    ),
  };
  const sections: PreCycleHomeSection[] = [
    {
      id: "understand-sperm-freezing",
      progressStepIds: ["learn"],
      render: () => <UnderstandSpermFreezingStep onAction={onAction} />,
    },
    {
      id: "know-sperm-baseline",
      progressStepIds: ["test"],
      render: () => (
        <KnowBaselineStep
          onAction={onAction}
          viewerIsSupporting={viewerIsSupporting}
        />
      ),
    },
    {
      id: "sperm-reset-program",
      progressStepIds: [],
      render: () => <SpermResetProgramStep onAction={onAction} />,
    },
    {
      id: "find-sperm-care",
      progressStepIds: [],
      render: () => (
        <EggFreezingClinicTimelineStep
          onAction={onAction}
          variant="sperm-freezing"
        />
      ),
    },
    {
      id: "sperm-analysis-skeleton",
      includeInPlanSetup: false,
      progressStepIds: [],
      render: () => (
        <SpermAnalysisSkeletonCard
          onAction={onAction}
          layout="single"
          // Before any analysis, the diagram is the explanation: what each
          // figure means, and a way to get them.
          emptyCard={{
            title: t(
              "homepage.sperm_analysis_skeleton.what_a_semen_analysis_tells_you",
            ),
            action: {
              label: t("homepage.sperm_analysis_skeleton.cta_get_analysis"),
              screen: "AtHomeFertilityTesting",
              params: { focus: "semen-analysis" },
            },
          }}
        />
      ),
    },
    {
      id: "start-sperm-banking",
      includeInPlanSetup: false,
      patientOnly: true,
      progressStepIds: ["freeze"],
      render: () => <SpermFreezingStartStep onAction={onAction} />,
    },
  ];

  return (
    <PreCycleHome
      activeStep={activeStep}
      steps={steps}
      completedSteps={completedSteps}
      totalSteps={totalSteps}
      eyebrow={heroContent.eyebrow}
      title={heroContent.title}
      snapshotItems={[
        {
          label: t("homepage.pre_cycle_egg_freezing.next"),
          value:
            activeStep?.title ??
            t("homepage.pre_cycle_egg_freezing.choose_next_step"),
        },
        {
          label: t("homepage.pre_cycle_sperm_freezing.reset_window"),
          value: t("homepage.pre_cycle_sperm_freezing.reset_window_value"),
        },
      ]}
      phaseDescription={heroContent.phaseDescription}
      intro={heroContent.intro}
      openPlanAccessibilityLabel={heroContent.openPlanAccessibilityLabel}
      planAccessibilityLabel={heroContent.planAccessibilityLabel}
      onAction={onAction}
      onOpenPlan={onOpenPlan}
      onEditPlan={onEditPlan}
      viewerIsSupporting={viewerIsSupporting}
      cycleEnding={cycleEnding}
      showStartCycleAction
      startCyclePreset="sperm_freezing"
      sections={sections}
    />
  );
}
